import { useState, useMemo } from 'react'
import { Alert, Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { supabase } from '../utils/supabase'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { L } from './ui/LavDropdown'
import {
	BK, BkIc, BkFlag, BkEmpty, BkPetLines,
	bkPetLineSx, bkSublineSx, bkIdColSx,
	bkApproveSx, bkGhostSx, bkIconBtnSx, bkDeclineSx, bkTimeChipSx,
	bkMoney, bkApptPrice, bkFullName, bkHit,
} from './bookings/bkBits'
import { usePetAvatars } from './bookings/usePetAvatars'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

// .bk-bar-link — quiet text action on the mobile card's footer row
const bkBarLinkSx = (danger = false) => ({
	display: 'inline-flex', alignItems: 'center', gap: '6px', height: 38, px: '10px',
	border: 'none', bgcolor: 'transparent', borderRadius: '7px', fontFamily: 'inherit',
	fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
	color: danger ? BK.danger : L.muted,
	'&:hover': danger
		? { bgcolor: BK.dangerSoft, color: BK.dangerDeep }
		: { bgcolor: L.primarySoft, color: L.primaryDeep },
	'&:disabled': { color: '#CFC9DE', bgcolor: 'transparent', cursor: 'default' },
})

/**
 * PendingAppointments — the Requests tab of the Bookings workspace, styled per
 * the Claude Design "Bookings · desktop" spec: `.bk-reqlist` of `.bk-req
 * rowstyle` rows on desktop, and the same design's stacked `.bk-req` card on
 * narrow screens.
 *
 * Behaviour is unchanged: a request is one group_id (one row per pet inside
 * it), Accept books the whole group, Deny cancels it, and an expired request
 * (its requested time has passed) can only be dismissed.
 */
function PendingAppointments({ merchant, onAppointmentClick, onMessageClient, appointments = [], query = '' }) {
	const [errorMessage, setErrorMessage] = useState('')
	const [processingGroupIds, setProcessingGroupIds] = useState(new Set())
	const theme = useTheme()
	const isNarrow = useMediaQuery(theme.breakpoints.down('lg'))

	// Pet photos for the request rows, resolved in one query.
	const petAvatars = usePetAvatars(useMemo(
		() => (appointments || []).map(a => a.pet_id).filter(Boolean),
		[appointments]
	))

	// Group appointments by group_id. Expired (past) groups stay in the list so
	// the merchant can clear them — they just get a Dismiss-only action.
	const groupedAppointments = useMemo(() => {
		const groups = new Map()
		const now = dayjs()

		appointments.forEach(appointment => {
			const groupKey = appointment.group_id || appointment.id // Use id as fallback if no group_id
			if (!groups.has(groupKey)) {
				groups.set(groupKey, [])
			}
			groups.get(groupKey).push(appointment)
		})

		// Sort appointments within each group by start_ts
		groups.forEach((groupAppts) => {
			groupAppts.sort((a, b) => new Date(a.start_ts) - new Date(b.start_ts))
		})

		const result = Array.from(groups.entries()).map(([groupId, groupAppts]) => ({
			groupId,
			appointments: groupAppts,
			// Use the first appointment's data for common fields
			firstAppointment: groupAppts[0],
			customerName: groupAppts[0].customer_name,
			employee: groupAppts[0].employees,
			// The requested time has passed — the request can only be dismissed.
			isExpired: dayjs(groupAppts[0].start_ts).isBefore(now)
		}))

		// Non-expired requests first (latest to earliest), expired ones at the bottom
		result.sort((a, b) => {
			if (a.isExpired !== b.isExpired) {
				return a.isExpired ? 1 : -1
			}
			return new Date(b.firstAppointment.start_ts) - new Date(a.firstAppointment.start_ts)
		})

		return result
	}, [appointments])

	// Header search — match on customer, pet names or service names.
	const visibleGroups = useMemo(() => {
		if (!query.trim()) return groupedAppointments
		return groupedAppointments.filter((g) => bkHit(
			query,
			g.customerName,
			...g.appointments.map((a) => a.pets?.name),
			...g.appointments.map((a) => a.service_types?.name),
		))
	}, [groupedAppointments, query])

	const handleAccept = async (group) => {
		const groupId = group.groupId
		setProcessingGroupIds(prev => new Set(prev).add(groupId))
		setErrorMessage('')

		try {
			// Update status to 'booked' for all appointments in the group
			const appointmentIds = group.appointments.map(a => a.id)

			const { error: updateError } = await supabase
				.from('appointments')
				.update({ status: 'booked' })
				.in('id', appointmentIds)

			if (updateError) throw updateError

			// Wait a moment for the database webhook to trigger the email
			// The webhook will automatically call send-appointment-email when status changes to 'booked'
			// The real-time subscription in NavigationSidebar will update the list
			await new Promise(resolve => setTimeout(resolve, 500))
		} catch (e) {
			console.error('Error accepting appointments:', e)
			setErrorMessage(e.message || 'Failed to accept appointments')
		} finally {
			setProcessingGroupIds(prev => {
				const newSet = new Set(prev)
				newSet.delete(groupId)
				return newSet
			})
		}
	}

	// Shared cancel path for Deny / Dismiss — the edge function cancels every
	// appointment in the group. `verb` only affects the error message wording.
	const cancelGroup = async (group, verb = 'deny') => {
		const groupId = group.groupId
		// Use the first appointment to cancel - the edge function will handle canceling all in the group
		const appointmentId = group.firstAppointment.id

		setProcessingGroupIds(prev => new Set(prev).add(groupId))
		setErrorMessage('')

		try {
			// Call cancel-appointment edge function - it handles canceling all in the group
			const { data, error } = await supabase.functions.invoke('cancel-appointment', {
				body: { appointmentId }
			})

			if (error) throw error

			if (!data.success) {
				throw new Error(data.error || 'Failed to cancel appointment')
			}

			// The real-time subscription in NavigationSidebar will update the list
		} catch (e) {
			console.error(`Error ${verb === 'dismiss' ? 'dismissing' : 'denying'} appointment:`, e)
			setErrorMessage(e.message || `Failed to ${verb} appointment`)
		} finally {
			setProcessingGroupIds(prev => {
				const newSet = new Set(prev)
				newSet.delete(groupId)
				return newSet
			})
		}
	}

	const handleDeny = async (group) => {
		const petNames = group.appointments.map(a => a.pets?.name).filter(Boolean).join(', ')
		const serviceNames = [...new Set(group.appointments.map(a => a.service_types.name))].join(', ')

		const confirmed = window.confirm(
			`Are you sure you want to deny this appointment?\n\n` +
			`Customer: ${group.customerName}\n` +
			`Pet(s): ${petNames || 'N/A'}\n` +
			`Service(s): ${serviceNames}\n` +
			`Time: ${dayjs(group.firstAppointment.start_ts).tz(merchant.timezone).format('MMM D, YYYY [at] h:mm A')}\n\n` +
			`This will cancel ${group.appointments.length > 1 ? 'all ' + group.appointments.length + ' appointments' : 'the appointment'} with no fees.`
		)

		if (!confirmed) return

		await cancelGroup(group, 'deny')
	}

	// Expired request — the requested time has already passed, so the only
	// action is to clear it. Same cancel call as Deny.
	const handleDismiss = async (group) => {
		await cancelGroup(group, 'dismiss')
	}

	if (errorMessage) {
		return (
			<Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
				{errorMessage}
			</Alert>
		)
	}

	if (visibleGroups.length === 0) {
		return query.trim() ? (
			<BkEmpty
				title={`No requests match “${query.trim()}”`}
				sub="Try a different name, pet or service."
			/>
		) : (
			<BkEmpty
				title="All caught up"
				sub="New booking requests from your booking page land here."
			/>
		)
	}

	const tz = merchant?.timezone

	// ── one request ───────────────────────────────────────────────────
	const renderGroup = (group) => {
		const isProcessing = processingGroupIds.has(group.groupId)
		const appts = group.appointments
		const isMultiplePets = appts.length > 1
		const isExpired = group.isExpired
		const first = group.firstAppointment
		const start = dayjs(first.start_ts).tz(tz)
		const end = dayjs(appts[appts.length - 1].end_ts).tz(tz)
		const totalMins = appts.reduce((s, a) => s + dayjs(a.end_ts).diff(dayjs(a.start_ts), 'minute'), 0)
		const totalPrice = appts.reduce((s, a) => s + bkApptPrice(a), 0)
		const groomer = bkFullName(group.employee)
		const note = first.comments

		// Per-pet blocks (design .bk-pets): each pet keeps its own breed,
		// service, minutes, price and add-ons — one add-on per line.
		const petLines = appts.map((a) => ({
			key: a.id,
			name: a.pets?.name || group.customerName,
			breed: a.pets?.breed || '',
			service: a.service_types?.name || '',
			mins: dayjs(a.end_ts).diff(dayjs(a.start_ts), 'minute'),
			price: bkApptPrice(a),
			addons: (Array.isArray(a.add_ons) ? a.add_ons : []).map(x => x.name).filter(Boolean),
			avatarUrl: petAvatars[a.pet_id],
		}))

		// "Requested 2h ago · phone" (design .bk-subline under the client name)
		const requestedLine = [
			first.created_at ? `Requested ${dayjs(first.created_at).fromNow()}` : null,
			first.phone_number || null,
		].filter(Boolean).join(' · ')

		const stop = (fn) => (e) => { e.stopPropagation(); fn() }
		const open = () => !isProcessing && onAppointmentClick && onAppointmentClick(first)

		const flags = (
			<>
				{isMultiplePets ? <BkFlag type="multi" label={`${appts.length} pets`} /> : null}
				{isExpired ? <BkFlag type="warn" label="Expired" /> : null}
			</>
		)

		// .bk-reqtime — requested time, warn-toned once it has passed
		const whenChip = (
			<Box sx={bkTimeChipSx(isExpired)}>
				<BkIc name={isExpired ? 'warn' : 'calendar'} size={13} sw={1.7} />
				<span>
					{start.format('ddd, MMM D')} · {start.format('h:mm A')}–{end.format('h:mm A')}
				</span>
			</Box>
		)

		// .bk-req-svc — inset box holding the per-pet blocks
		const petBox = (
			<Box sx={{
				display: 'flex', flexDirection: 'column', gap: '7px', p: '12px 14px',
				bgcolor: BK.inset, border: `1px solid ${BK.insetBorder}`, borderRadius: '12px',
			}}>
				<BkPetLines pets={petLines} />
			</Box>
		)

		const noteBlock = note ? (
			<Box component="p" sx={{
				m: 0, fontSize: '12.5px', fontWeight: 600, color: BK.body, lineHeight: 1.5,
			}}>
				“{note}”
			</Box>
		) : null

		const spin = <CircularProgress size={15} sx={{ color: 'inherit' }} />
		const canMessage = Boolean(onMessageClient && first.user_id)
		const message = () => onMessageClient({ id: first.user_id })

		// .bk-reqact — desktop action cluster: Accept, quiet Deny text button,
		// message icon (Dismiss replaces both when the request expired).
		const desktopActions = (
			<>
				{isExpired ? (
					<Box
						component="button" type="button" disabled={isProcessing}
						onClick={stop(() => handleDismiss(group))}
						sx={{ ...bkGhostSx(true), height: 36 }}
					>
						{isProcessing ? spin : 'Dismiss'}
					</Box>
				) : (
					<>
						<Box
							component="button" type="button" disabled={isProcessing}
							title={`Books ${start.format('ddd, MMM D')} at ${start.format('h:mm A')}`}
							onClick={stop(() => handleAccept(group))}
							sx={bkApproveSx()}
						>
							{isProcessing ? spin : <><BkIc name="check" size={14} sw={2.1} /><span>Accept</span></>}
						</Box>
						<Box
							component="button" type="button" disabled={isProcessing} title="Deny request"
							onClick={stop(() => handleDeny(group))}
							sx={{ ...bkDeclineSx, height: 36, px: '12px' }}
						>
							{isProcessing ? spin : 'Deny'}
						</Box>
					</>
				)}
				{canMessage ? (
					<Box
						component="button" type="button" disabled={isProcessing}
						title={`Message ${group.customerName}`}
						aria-label={`Message ${group.customerName}`}
						onClick={stop(message)}
						sx={{ ...bkIconBtnSx(false), width: 36, height: 36, borderRadius: '9px' }}
					>
						<BkIc name="msg" size={14} />
					</Box>
				) : null}
			</>
		)

		// ── narrow: the design's mobile card (.bkm-req) ─────────────────
		// Header (client + ago), one "what they're asking for" inset (requested
		// time + per-pet blocks + totals foot), the note as plain text, a
		// full-width Accept & book, then quiet Message / Deny links.
		if (isNarrow) {
			return (
				<Box
					key={group.groupId}
					component="article"
					onClick={open}
					sx={{
						position: 'relative', bgcolor: BK.card, border: `1px solid ${L.border}`,
						borderRadius: '16px', boxShadow: BK.cardShadow, p: '14px',
						display: 'flex', flexDirection: 'column', gap: '12px',
						cursor: onAppointmentClick ? 'pointer' : 'default', opacity: isProcessing ? 0.65 : 1,
					}}
				>
					{/* .bkm-req-top */}
					<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '11px' }}>
						<Box sx={{ ...bkIdColSx, flex: 1 }}>
							<Box sx={bkPetLineSx}>
								<strong>{group.customerName}</strong>
								{flags}
							</Box>
							{first.phone_number ? (
								<Box component="span" sx={bkSublineSx}>{first.phone_number}</Box>
							) : null}
						</Box>
						{first.created_at ? (
							<Box component="span" sx={{ flex: 'none', fontSize: '11.5px', fontWeight: 650, color: BK.subtle, pt: '3px' }}>
								{dayjs(first.created_at).fromNow()}
							</Box>
						) : null}
					</Box>

					{/* .bkm-req-ask — requested time + per-pet blocks + totals */}
					<Box sx={{
						display: 'flex', flexDirection: 'column', gap: '8px', p: '11px 12px 10px',
						bgcolor: BK.inset, border: `1px solid ${BK.insetBorder}`, borderRadius: '12px',
					}}>
						<Box sx={{
							display: 'flex', alignItems: 'center', gap: '7px',
							color: isExpired ? BK.warnText : L.primaryDeep,
						}}>
							<BkIc name={isExpired ? 'warn' : 'calendar'} size={13} sw={1.8} />
							<Box component="strong" sx={{ fontSize: '14px', fontWeight: 800, letterSpacing: '-0.01em' }}>
								{start.format('ddd, MMM D')} · {start.format('h:mm A')}–{end.format('h:mm A')}
							</Box>
						</Box>
						<BkPetLines pets={petLines} />
						<Box sx={{
							display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px',
							borderTop: `1px solid ${BK.dayBorder}`, pt: '8px',
							fontSize: '12px', fontWeight: 650, color: L.muted,
						}}>
							<span>
								{appts.length} pet{isMultiplePets ? 's' : ''} · {totalMins} min{groomer ? ` · with ${groomer}` : ''}
							</span>
							<Box component="strong" sx={{ fontSize: '15px', fontWeight: 800, color: L.text, fontVariantNumeric: 'tabular-nums' }}>
								{bkMoney(totalPrice)}
							</Box>
						</Box>
					</Box>

					{noteBlock}

					{/* .bkm-req-accept — the one primary CTA (Dismiss when expired) */}
					{isExpired ? (
						<Box
							component="button" type="button" disabled={isProcessing}
							onClick={stop(() => handleDismiss(group))}
							sx={{ ...bkGhostSx(false), height: 46, borderRadius: '12px', justifyContent: 'center' }}
						>
							{isProcessing ? spin : 'Dismiss request'}
						</Box>
					) : (
						<Box
							component="button" type="button" disabled={isProcessing}
							title={`Books ${start.format('ddd, MMM D')} at ${start.format('h:mm A')}`}
							onClick={stop(() => handleAccept(group))}
							sx={{
								display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
								height: 46, border: 'none', borderRadius: '12px', fontFamily: 'inherit',
								bgcolor: L.primary, color: L.onPrimary, fontSize: '14.5px', fontWeight: 750,
								boxShadow: '0 2px 10px rgba(124,107,217,0.30)', whiteSpace: 'nowrap', cursor: 'pointer',
								'&:hover': { bgcolor: L.primaryDeep },
								'&:disabled': { bgcolor: L.primarySoft, color: '#A99DDD', boxShadow: 'none', cursor: 'default' },
							}}
						>
							{isProcessing ? spin : <><BkIc name="check" size={15} sw={2.2} /><span>Accept & book</span></>}
						</Box>
					)}

					{/* .bkm-req-alt — quiet secondary actions */}
					{(canMessage || !isExpired) && (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', m: '-4px -4px -2px' }}>
							{canMessage ? (
								<Box component="button" type="button" disabled={isProcessing}
									onClick={stop(message)} sx={bkBarLinkSx(false)}>
									<BkIc name="msg" size={13} />
									<span>Message {group.customerName.split(/\s+/)[0]}</span>
								</Box>
							) : <span />}
							{!isExpired && (
								<Box component="button" type="button" disabled={isProcessing}
									onClick={stop(() => handleDeny(group))} sx={bkBarLinkSx(true)}>
									Deny
								</Box>
							)}
						</Box>
					)}
				</Box>
			)
		}

		// ── desktop: .bk-req.rowstyle — header (who · summary · actions)
		// over the full-width per-pet box, then the note ─────────────────
		return (
			<Box
				key={group.groupId}
				component="article"
				onClick={open}
				sx={{
					position: 'relative', bgcolor: BK.card, border: `1px solid ${L.border}`,
					borderRadius: '14px', boxShadow: BK.cardShadow, p: '16px',
					display: 'flex', flexDirection: 'column', gap: '12px',
					cursor: onAppointmentClick ? 'pointer' : 'default', opacity: isProcessing ? 0.65 : 1,
					'&:hover': { borderColor: onAppointmentClick ? L.primary : L.border },
				}}
			>
				{/* .bk-req-head */}
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px 16px', flexWrap: 'wrap' }}>
					<Box sx={{ ...bkIdColSx, gap: '4px', flex: 1, minWidth: 168 }}>
						<Box sx={{ ...bkPetLineSx, '& > strong': { ...bkPetLineSx['& > strong'], fontSize: '14.5px' } }}>
							<strong>{group.customerName}</strong>
							{flags}
						</Box>
						{requestedLine ? <Box component="span" sx={bkSublineSx}>{requestedLine}</Box> : null}
					</Box>
					{/* .bk-reqsum */}
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flex: 'none' }}>
						{whenChip}
						<Box component="span" sx={{
							fontSize: '12px', fontWeight: 650, color: L.muted,
							fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
						}}>
							{appts.length} pet{isMultiplePets ? 's' : ''} · {totalMins} min ·{' '}
							<Box component="strong" sx={{ color: L.text, fontWeight: 800, fontSize: '13.5px' }}>
								{bkMoney(totalPrice)}
							</Box>
						</Box>
						{groomer ? (
							<Box component="span" sx={{
								display: 'inline-flex', alignItems: 'center', gap: '5px',
								fontSize: '11.5px', fontWeight: 650, color: BK.breed, whiteSpace: 'nowrap',
							}}>
								<BkIc name="user" size={11} sw={1.8} />
								<span>With {groomer}</span>
							</Box>
						) : null}
					</Box>
					{/* .bk-reqact */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
						{desktopActions}
					</Box>
				</Box>

				{petBox}
				{noteBlock}
			</Box>
		)
	}

	// .bk-reqlist
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: L.font, color: L.text }}>
			{visibleGroups.map(renderGroup)}
		</Box>
	)
}

export default PendingAppointments
